import React, { useEffect, useState } from "react";
import { Accordion, AccordionTab } from "primereact/accordion";
import { useLocation, useNavigate, useNavigation } from "react-router-dom";
import { getStoreLocalData, getUserData } from "../../../utils/localStorage";
import { formatDate } from "../../../constants/DateUtil/DateUtil";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import { getCustomerOldOrderLedger } from "../../../services/CustomerInfo/OldOrder";
import { DataView } from "primereact/dataview";
import StoreMenu from "../../StoreMenu";
import { ProgressSpinner } from "primereact/progressspinner";
import OldOrderInfoEdit from "./OldOrderInfoEdit";

const CustomerOldOrderLedger = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { customerData, storeCustomerId, storeIdValue } = location.state || {}; // Fallback to prevent undefined errors

  const { brandProfileData, userProfileData } = getUserData();
  const { storeLocalData } = getStoreLocalData();

  const brandId = brandProfileData.id;

  const [oldOrderInfoEditVisible, setOldOrderInfoEditVisible] = useState(false);
  const [oldOrderInfoEditVisibleData, setOldOrderInfoEditVisibleData] =
    useState({});
  const [customerOldOrderLedgerData, setCustomerOldOrderLedgerData] = useState(
    {}
  );

  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const initializeVariable = () => {
    setCustomerOldOrderLedgerData({});
  };

  const fetchCustomerOldOrderLedger = (data) => {
    setLoading(true);
    getCustomerOldOrderLedger(brandId, data)
      .then((response) => {
        if (!response.data.error) {
          const Res = response.data;

          setCustomerOldOrderLedgerData(Res);
          setLoading(false);
        }
      })
      .catch((err) => {
        console.error("Error:", err);
      });
  };

  useEffect(() => {
    initializeVariable();
    const data = {
      brandId: brandId,
      storeCustomerIdIn: [storeCustomerId],
    };
    fetchCustomerOldOrderLedger(data);
  }, []);

  const receiveResponse = ({ message, success }) => {
    if (success === true) {
      const data = {
        brandId: brandId,
        storeCustomerIdIn: [storeCustomerId],
      };
      fetchCustomerOldOrderLedger(data);
    }
  };

  const filterOrderInfoList = () => {
    if (!searchTerm) return customerOldOrderLedgerData?.orderList; // If no search term, return all orders

    return customerOldOrderLedgerData?.orderList.filter((item) => {
      const term = searchTerm.toLowerCase();

      return item.invoiceNo?.toLowerCase().includes(term);
    });
  };

  const filteredOrderInfoData = filterOrderInfoList();

  const handleClearSearch = () => {
    setSearchTerm("");
  };

  const OrderStatusColors = {
    BOOKED: "#3EB3E1",
    INPROCESS: "#866CCE",
    PROCESSED: "#F5C43A",
    DELIVERED: "#09B4AA",
    DONE: "#55BD5A",
    CANCEL: "#FF6767",
    UNPROCESSED: "#5D53D8",
    OUT_FOR_DELIVERY: "#6EA4F5",
    DELAY_STATUS: "#E89534",
    IN_WAREHOUSE: "#a906c2",
    FACTORY: "#E89534",
    DEFAULT: "#000",
  };

  const itemTemplate = (order, index) => {
    const orderStatusColor =
      OrderStatusColors[order.status] || OrderStatusColors.DEFAULT;
    const formattedCreatedDate = formatDate(order.createdDate);
    const formattedDeliveryDate = formatDate(order.dateOfDelivery);
    return (
      <Accordion>
        <AccordionTab
          key={index}
          style={{
            "--backgroundColor-AccordionTab": orderStatusColor,
          }}
          header={`${
            order.invoiceNo || "N/A"
          } | ${formattedCreatedDate}  ${order.status.replace(/_/g, " ")}`}
        >
          <div className="cursor-pointer">
            <div className="flex-row">
              <div className="col-3">
                <p className="text-base font-semiBold capitalize">
                  Delivery Date:
                </p>
                <p className="text-lg font-bold capitalize">
                  {formattedDeliveryDate}
                </p>
              </div>
              <div className="col-3">
                <p className="text-base font-semiBold capitalize">
                  Garment Count:
                </p>
                <p className="text-lg font-bold capitalize">
                  {order.garmentCount}
                </p>
              </div>
              <div className="col-3">
                <p className="text-base font-semiBold capitalize">
                  Order Amount:
                </p>
                <p className="text-lg font-bold capitalize">
                  {storeLocalData?.currencySymbol} {order.orderAmount}
                </p>
              </div>
              <div className="col-3 flex justify-content-between">
                <div>
                  <p className="text-base font-semiBold capitalize">
                    Paid Amount:
                  </p>
                  <p className="text-lg font-bold capitalize">
                    {storeLocalData?.currencySymbol} {order.paidAmount}
                  </p>
                </div>
                <div
                  className="mr-4 mt-2"
                  onClick={() => {
                    setOldOrderInfoEditVisible(true);
                    setOldOrderInfoEditVisibleData(order);
                  }}
                >
                  <i
                    className="pi pi-pencil"
                    style={{
                      fontSize: "20px",
                      cursor: "pointer",
                      color: "#2B70D7",
                    }}
                  ></i>
                </div>
              </div>
            </div>

            <div className="flex-row">
              <div className="col-3">
                <p className="text-base font-semiBold capitalize">Discount:</p>
                <p className="text-lg font-bold capitalize">
                  {storeLocalData?.currencySymbol} {order.discount}
                </p>
              </div>
              <div className="col-3">
                <p className="text-base font-semiBold capitalize">Balance:</p>
                <p className="text-lg font-bold capitalize">
                  {storeLocalData?.currencySymbol} {order.balanceAmount}
                </p>
              </div>
              <div className="col-3">
                <p className="text-base font-semiBold capitalize">Remark:</p>
                <p className="text-lg font-bold capitalize">{order.remark}</p>
              </div>
            </div>
          </div>
        </AccordionTab>
      </Accordion>
    );
  };

  const listTemplate = (items) => {
    if (!items || items.length === 0) return null;

    let list = items.map((product, index) => {
      return itemTemplate(product, index);
    });

    return <div>{list}</div>;
  };

  return (
    <div className="flex-1">
      <div
        className="h-3rem surface-0 sticky z-2 top-0 flex justify-content-center align-items-center"
        style={{
          "--surface-0": "#1752AA",
        }}
      >
        <div className="flex-1 flex ml-3 mr-3">
          <div className="flex-1 flex ">
            <div className="ml-3 flex justify-content-center align-items-center">
              <div className="h-1rem w-1rem border-circle flex justify-content-center align-items-center">
                <i
                  className="pi pi-angle-left"
                  onClick={() =>
                    navigate("/WalletLedgerSelection", {
                      state: {
                        customerData: customerData,
                        storeCustomerId: storeCustomerId,
                        storeId: storeIdValue,
                      },
                    })
                  }
                  style={{ fontSize: "27px", cursor: "pointer", color: "#fff" }}
                ></i>
              </div>
            </div>
            <div className="flex-1 ml-5">
              <p className="text-white text-3xl font-bold uppercase">
                Old Orders
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-4 my-4">
        <div
          className="flex gap-3 mb-3 pb-1 new-scroll-bar"
          style={{
            overflowX: "auto",
            whiteSpace: "nowrap",
          }}
        >
          <div
            style={{ backgroundColor: "#09B4AA" }}
            className="px-3 py-2 border-round-md"
          >
            <p className="text-white text-xl font-bold uppercase">
              Sale - {storeLocalData?.currencySymbol} {customerOldOrderLedgerData.totalSale}
            </p>
          </div>
          <div
            style={{ backgroundColor: "#5D53D8" }}
            className="px-3 py-2 border-round-md"
          >
            <p className="text-white text-xl font-bold uppercase">
              Discount - {storeLocalData?.currencySymbol} {customerOldOrderLedgerData.totalDiscount}
            </p>
          </div>
          <div
            style={{ backgroundColor: "#55BD5A" }}
            className="px-3 py-2 border-round-md"
          >
            <p className="text-white text-xl font-bold uppercase">
              Paid - {storeLocalData?.currencySymbol} {customerOldOrderLedgerData.totalPaid}
            </p>
          </div>
          <div
            style={{ backgroundColor: "#2B70D7" }}
            className="px-3 py-2 border-round-md"
          >
            <p className="text-white text-xl font-bold uppercase">
              Balance - {storeLocalData?.currencySymbol} {customerOldOrderLedgerData.totalBalance}
            </p>
          </div>
        </div>
        <div className="my-2 mx-1 flex flex-1">
          <div className="flex-1">
            <IconField iconPosition="left" className="flex flex-1">
              <InputIcon className="pi pi-search text-xl"> </InputIcon>
              <InputText
                placeholder={"Search By Invoice No."}
                value={searchTerm}
                keyboardType={"numeric"}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="text-xl flex flex-1 py-1 px-5"
                style={{ borderRadius: 30 }}
              />
            </IconField>
          </div>

          {searchTerm && (
            <div
              className="flex justify-content-center align-items-center mx-2 cursor-pointer"
              onClick={handleClearSearch}
            >
              <p className="text-4xl font-bold " style={{ color: "red" }}>
                X
              </p>
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex flex-1 flex-column justify-content-center align-items-center">
            <ProgressSpinner />
            <p className="text-3xl font-bold">Loading Old Orders...</p>
          </div>
        ) : customerOldOrderLedgerData?.orderList?.length > 0 ? (
          <DataView
            value={
              filteredOrderInfoData || customerOldOrderLedgerData?.orderList
            }
            listTemplate={listTemplate}
            paginator
            rows={20}
          />
        ) : (
          <div className="flex flex-1 justify-content-center align-items-center">
            <p className="text-3xl font-bold">No Orders Found</p>
          </div>
        )}
      </div>
      <OldOrderInfoEdit
        setOldOrderInfoEditVisible={setOldOrderInfoEditVisible}
        oldOrderInfoEditVisible={oldOrderInfoEditVisible}
        customerOrdersData={oldOrderInfoEditVisibleData}
        sendResponse={receiveResponse}
        userProfileData={userProfileData}
      />
    </div>
  );
};

export default CustomerOldOrderLedger;
